import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChatHeader from "../../components/chat-header";

import {
  notificationService,
  TeacherNotificationItem,
} from "../../services/notifications/notification.service";
import { useNotificationStore } from "../../utils/notificationStore";
import { showSuccessToast, showErrorToast } from "../../components/Toaster";
import { NotificationCard } from "./components/NotificationCard";
import { NotificationDetailsModal } from "./components/NotificationDetailsModal";
import "./notifications.scss";

const PAGE_SIZE = 20;

export const NotificationCenter: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<TeacherNotificationItem[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<TeacherNotificationItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const { unreadCount, decrementUnreadCount, fetchUnreadCount } =
    useNotificationStore();

  const prevUnreadCountRef = useRef<number>(unreadCount);

  const loadNotifications = useCallback(
    async (pageNum: number, isInitial = false, isSilent = false) => {
      try {
        if (isInitial) {
          setIsLoading(true);
        } else if (!isSilent) {
          setIsFetchingMore(true);
        }

        const response = await notificationService.getNotifications(pageNum, PAGE_SIZE);
        const items = response?.notifications || (response as any)?.data || [];

        if (pageNum === 1) {
          if (isSilent) {
            setNotifications((prev) => {
              // If user has loaded multiple pages, preserve older items below the fresh top page
              if (prev.length > PAGE_SIZE) {
                const incomingIds = new Set(items.map((i) => String(i.id)));
                const olderItems = prev.filter((i) => !incomingIds.has(String(i.id)));
                return [...items, ...olderItems];
              }
              return items;
            });
          } else {
            setNotifications(items);
          }
        } else {
          setNotifications((prev) => [...prev, ...items]);
        }

        setHasMore(items.length >= PAGE_SIZE);
        setPage(pageNum);
      } catch (err) {
        console.error("Failed to load notifications:", err);
        if (!isSilent) {
          showErrorToast(t("errorOccurredTryAgain"));
        }
      } finally {
        if (isInitial) {
          setIsLoading(false);
        } else if (!isSilent) {
          setIsFetchingMore(false);
        }
      }
    },
    [t]
  );

  // Initial load
  useEffect(() => {
    loadNotifications(1, true);
    fetchUnreadCount();
  }, [loadNotifications, fetchUnreadCount]);

  // Reactive silent refresh: automatically refresh the list when a new incoming message arrives
  useEffect(() => {
    if (unreadCount > prevUnreadCountRef.current) {
      loadNotifications(1, false, true);
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, loadNotifications]);

  const handleMarkAsRead = async (id: number | string) => {
    const target = notifications.find((n) => String(n.id) === String(id));
    if (!target || target.is_read) return;

    // Optimistic local update
    setNotifications((prev) =>
      prev.map((n) =>
        String(n.id) === String(id) ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      )
    );
    decrementUnreadCount?.();

    try {
      await notificationService.markAsRead(id);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      // Revert if error
      setNotifications((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, is_read: false, read_at: null } : n))
      );
      fetchUnreadCount();
    }
  };

  const handleDelete = async (id: number | string) => {
    const target = notifications.find((n) => String(n.id) === String(id));
    if (!target) return;

    // Optimistic removal
    setNotifications((prev) => prev.filter((n) => String(n.id) !== String(id)));
    if (!target.is_read) {
      decrementUnreadCount?.();
    }
    if (selectedNotification && String(selectedNotification.id) === String(id)) {
      setSelectedNotification(null);
    }

    try {
      await notificationService.deleteNotification(id);
      showSuccessToast(t("notificationDeleted"));
    } catch (err) {
      console.error("Failed to delete notification:", err);
      showErrorToast(t("errorOccurredTryAgain"));
      loadNotifications(1);
    }
  };

  const handleLoadMore = () => {
    if (!isLoading && !isFetchingMore && hasMore) {
      loadNotifications(page + 1);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const unreadList = notifications.filter((n) => !n.is_read);
  const readList = notifications.filter((n) => n.is_read);

  return (
    <div className={`notifications-page ${selectedNotification ? "is-modal-open" : ""}`}>
      <ChatHeader
        chatHeading={t("notificationsFromTeacher")}
        onBackClick={handleBack}
      />
      <div className="notifications-page-container">

      {/* Skeleton Loading State */}
      {isLoading ? (
        <div className="notifications-skeleton-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="notification-card notification-card--skeleton">
              <div className="notification-card__avatar notification-skeleton-shimmer" />
              <div className="notification-card__content">
                <div className="notification-card__header-row">
                  <div className="notification-skeleton-text notification-skeleton-text--name notification-skeleton-shimmer" />
                  <div className="notification-skeleton-text notification-skeleton-text--time notification-skeleton-shimmer" />
                </div>
                <div className="notification-skeleton-text notification-skeleton-text--body-1 notification-skeleton-shimmer" />
                <div className="notification-skeleton-text notification-skeleton-text--body-2 notification-skeleton-shimmer" />
              </div>
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        /* Empty State */
        <div className="notifications-empty-state">
          <div className="empty-icon-wrapper">
            <MailOutlineIcon className="empty-icon" />
          </div>
          <h3 className="empty-title">{t("noNotifications")}</h3>
          <p className="empty-desc">{t("noNotificationsDesc")}</p>
        </div>
      ) : (
        /* Notification Sections */
        <>
          {/* Unread Section */}
          <div className="notifications-section">
            <div className="notifications-section__title">
              <span>{t("unreadNotifications")}</span>
              {unreadList.length > 0 && (
                <span className="section-count-badge">{unreadList.length}</span>
              )}
            </div>
            {unreadList.length > 0 ? (
              <div className="notifications-list">
                {unreadList.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    onSelect={setSelectedNotification}
                  />
                ))}
              </div>
            ) : (
              <div className="notifications-section__empty-text">
                {t("noNewNotifications")}
              </div>
            )}
          </div>

          {/* Read / Previous Section */}
          {readList.length > 0 && (
            <div className="notifications-section">
              <div className="notifications-section__title">
                <span>{t("readNotifications")}</span>
              </div>
              <div className="notifications-list">
                {readList.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    onSelect={setSelectedNotification}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Load More Button */}
          {hasMore && (
            <div className="notifications-load-more">
              <button
                type="button"
                className="load-more-btn"
                onClick={handleLoadMore}
                disabled={isFetchingMore}
              >
                {isFetchingMore ? "..." : t("loadMore")}
              </button>
            </div>
          )}
        </>
      )}
        <div className="notifications-bottom-spacer" />
      </div>

      {/* Notification Details Bottom Sheet Popup */}
      <NotificationDetailsModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default NotificationCenter;
